import React, { useState, useEffect, useMemo } from 'react';
import { Container, ListItemText, Paper, Typography, Box, Chip, Stack, List, ListItem, ListItemIcon } from "@mui/material";
import { useApplicationContext } from "ApplicationContext";
import { useDocumentTitle } from "Hooks/useDocumentTitle";
import { DocumentationFilter, EVersionStage, GetFileTypeData, GetVersionFileData } from 'apiClient/Api';
import IconPlaceholder from 'Pages/Admin/IconPlaceholder';
import { useLocation, useParams } from 'react-router';
import { useTranslation } from "react-i18next";
import { MarkdownRenderer } from '../../Components/MarkdownRenderer';
import DescriptionIcon from '@mui/icons-material/Description';
import { DownloadFileButton } from 'Components/DownloadFileButton';
import {SupportIcon} from "../../SupportIcon";

type HostSystemOption = {
    id: string;
    name: string;
};

/**
 * Applications component to display a single application.
 * @returns {JSX.Element} The rendered component.
 */
export const ApplicationSingle = (): JSX.Element => {
    const { t } = useTranslation();
    const [fileTypeData, setFileTypeData] = useState<GetFileTypeData | null>(null);
    const [versionFiles, setVersionFiles] = useState<GetVersionFileData[]  | null>(null);
    const [documentationFiles, setDocumentationFiles] = useState<GetVersionFileData[]  | null>(null);
    const [selectedHostSystems, setSelectedHostSystems] = useState<string[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const { application } = useParams<{ application: string }>();
    const location = useLocation();
    const context = useApplicationContext();
    const controller = context.api.fileType;
    useDocumentTitle(fileTypeData?.name ?? "Anwendung");

    const hostSystemOptions = useMemo<HostSystemOption[]>(() => {
        if (!versionFiles) {
            return [];
        }
        const optionsMap = new Map<string, string>();
        versionFiles.forEach((file) => {
            if (file.hostSystemId) {
                const previousValue = optionsMap.get(file.hostSystemId);
                const nextName = file.hostSystemName ?? previousValue ?? '';
                optionsMap.set(file.hostSystemId, nextName);
            }
        });
        return Array.from(optionsMap.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [versionFiles]);

    const filteredVersionFiles = useMemo(() => {
        if (!versionFiles) {
            return [];
        }
        if (selectedHostSystems.length === 0) {
            return versionFiles;
        }
        return versionFiles.filter((file) => file.hostSystemId && selectedHostSystems.includes(file.hostSystemId));
    }, [versionFiles, selectedHostSystems]);

    /**
     * Resolves a translated stage label.
     * @param stage The version stage.
     * @returns {string} Stage label.
     */
    const getStageLabel = (stage?: EVersionStage): string => {
        switch (stage) {
            case EVersionStage.INTERNAL:
                return t("versionStage.internal");
            case EVersionStage.ALPHA:
                return t("versionStage.alpha");
            case EVersionStage.BETA:
                return t("versionStage.beta");
            case EVersionStage.RELEASE_CANDIDATE:
                return t("versionStage.release_candidate");
            case EVersionStage.RELEASE:
                return t("versionStage.release");
            case EVersionStage.APPROVED:
                return t("versionStage.approved");
            default:
                return "";
        }
    };

    /**
     * Extracts host system filters from the query string.
     * @param search The location search string.
     * @returns {string[]} Host system IDs from the query.
     */
    const getHostSystemsFromSearch = (search: string): string[] => {
        if (!search) {
            return [];
        }
        const params = new URLSearchParams(search);
        return params.getAll("system").filter(Boolean);
    };

    useEffect(() => {
        setSelectedHostSystems(getHostSystemsFromSearch(location.search));
    }, [application, location.search]);

    useEffect(() => {
        if (hostSystemOptions.length === 0) {
            return;
        }
        setSelectedHostSystems((prev) => prev.filter((id) => hostSystemOptions.some((option) => option.id === id)));
    }, [hostSystemOptions]);

    /**
     * Toggles a host system filter chip.
     * @param hostSystemId Host system identifier.
     */
    const toggleHostSystem = (hostSystemId: string) => {
        setSelectedHostSystems((prev) => (prev.includes(hostSystemId)
            ? prev.filter((id) => id !== hostSystemId)
            : [...prev, hostSystemId]
        ));
    };




    useEffect(() => {
        if (application) {
            if( context.signInState){
                controller.fileTypeGetFileType(application).then(data => {
                    if (data.data) {
                        setFileTypeData(data.data);
                    } else {
                        setFileTypeData(null);
                    }
                    setLoading(false);
                }).catch(error => {
                    context.apiError(error);
                    setLoading(false);
                });

                controller.fileTypeGetFileTypeVersions(application, {documentationFilter: DocumentationFilter.ExcludeDocumentation}).then(data => {
                    if (data.data) {
                        setVersionFiles(data.data);
                    } else {
                        setVersionFiles(null);
                    }
                    setLoading(false);
                }).catch(error => {
                    context.apiError(error);
                    setLoading(false);
                });

                controller.fileTypeGetFileTypeVersions(application, {documentationFilter: DocumentationFilter.OnlyDocumentation}).then(data => {
                    if (data.data) {
                        setDocumentationFiles(data.data);
                    } else {
                        setDocumentationFiles(null);
                    }
                }).catch(error => {
                    context.apiError(error);
                });
            }
            else{
                controller.fileTypeGetFileTypePublic(application).then(data => {
                    if (data.data) {
                        setFileTypeData(data.data);
                    } else {
                        setFileTypeData(null);
                    }
                    setLoading(false);
                }).catch(error => {
                    context.apiError(error);
                    setLoading(false);
                });
                setVersionFiles(null);
                setDocumentationFiles(null);
            }
        }
    }, [application, context.signInState]);

    if (loading) {
        return (
            <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
                <SupportIcon />
            </Container>
        );
    }

    if (!fileTypeData) {
        return (
            <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
                <Typography variant="h5" component={"h2"}>{t('base.pages.applicationNotFound')}</Typography>
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
            <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
                <Box display="flex" alignItems="center" sx={{mb: 2}}>
                <IconPlaceholder size={100} iconId={fileTypeData.iconId} />
                <Box sx={{ml: 2}}>
                    <Typography variant="h4" component={"h1"}>{fileTypeData.name}</Typography>
                    <Typography variant="caption" component={"p"}>{fileTypeData.platformConfigurations?.[0]?.hostSystemName}</Typography>
                    <Stack direction="row" 
                        spacing={1}
                        useFlexGap
                        sx={{ flexWrap: 'wrap' }}
                        >
                       {fileTypeData.filterTags?.map(tag => (
                           <Chip key={tag.identifier} label={tag.name} />
                       ))}
                    </Stack>
                    {fileTypeData.variants && fileTypeData.variants.length > 0 && (
                        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1 }}>
                            {fileTypeData.variants
                                .map((variant) => variant.tag ?? variant.name)
                                .filter((label): label is string => Boolean(label))
                                .map((label) => (
                                    <Chip key={`${fileTypeData.id}-${label}`} label={label} size="small" variant="outlined" />
                                ))}
                        </Stack>
                    )}
                </Box>
                </Box>
                {versionFiles != null && versionFiles.length > 0 && (
                    <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
                            <Typography variant="h5" component={"h2"}>{t('base.pages.versions')}</Typography>
                            {hostSystemOptions.length > 0 && (
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                    {hostSystemOptions.map((system) => (
                                        <Chip
                                            key={system.id}
                                            label={system.name || system.id}
                                            color={selectedHostSystems.includes(system.id) ? "primary" : "default"}
                                            variant={selectedHostSystems.includes(system.id) ? "filled" : "outlined"}
                                            onClick={() => toggleHostSystem(system.id)}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                        <List>
                            {filteredVersionFiles.map((versionFile) => {
                                const hostSystemLabel = versionFile.hostSystemName
                                    ? t('applications.hostSystem', { host: versionFile.hostSystemName })
                                    : null;
                                const stageLabel = getStageLabel((versionFile as { stage?: EVersionStage }).stage);

                                return (
                                    <DownloadFileButton key={versionFile.id} id={versionFile.id!} fileName={versionFile.fileName!}>
                                        <ListItem
                                            secondaryAction={(hostSystemLabel || stageLabel) ? (
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {hostSystemLabel && (
                                                        <Chip
                                                            label={versionFile.hostSystemName}
                                                            size="small"
                                                            variant="outlined"
                                                            aria-label={hostSystemLabel}
                                                            title={hostSystemLabel}
                                                            onMouseDown={(event) => event.stopPropagation()}
                                                            onClick={(event) => event.stopPropagation()}
                                                        />
                                                    )}
                                                    {stageLabel && (
                                                        <Chip
                                                            label={stageLabel}
                                                            size="small"
                                                            variant="outlined"
                                                            onMouseDown={(event) => event.stopPropagation()}
                                                            onClick={(event) => event.stopPropagation()}
                                                        />
                                                    )}
                                                </Stack>
                                            ) : undefined}
                                        >
                                            <ListItemIcon>
                                                <DescriptionIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${versionFile.versionKey?.major}.${versionFile.versionKey?.minor}.${versionFile.versionKey?.patch}`}
                                                secondary={versionFile.fileName}
                                            />
                                        </ListItem>
                                    </DownloadFileButton>
                                );
                            })}
                        </List>
                    </>
                )}
                {documentationFiles != null && documentationFiles.length > 0 && (
                    <>
                        <Typography variant="h5" component={"h2"} sx={{ mt: 4 }}>{t('base.pages.documentation')}</Typography>
                        <List>
                            {documentationFiles.map((docFile) => {
                                const versionNumber = `${docFile.versionKey?.major}.${docFile.versionKey?.minor}.${docFile.versionKey?.patch}`;
                                const stageLabel = getStageLabel((docFile as { stage?: EVersionStage }).stage);

                                return (
                                    <DownloadFileButton key={docFile.id} id={docFile.id!} fileName={docFile.fileName!}>
                                        <ListItem
                                            secondaryAction={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Chip
                                                        label={versionNumber}
                                                        size="small"
                                                        variant="outlined"
                                                        onMouseDown={(event) => event.stopPropagation()}
                                                        onClick={(event) => event.stopPropagation()}
                                                    />
                                                    {stageLabel && (
                                                        <Chip
                                                            label={stageLabel}
                                                            size="small"
                                                            variant="outlined"
                                                            onMouseDown={(event) => event.stopPropagation()}
                                                            onClick={(event) => event.stopPropagation()}
                                                        />
                                                    )}
                                                </Stack>
                                            }
                                        >
                                            <ListItemIcon>
                                                <DescriptionIcon />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={docFile.fileName}
                                                secondary={docFile.hostSystemName}
                                            />
                                        </ListItem>
                                    </DownloadFileButton>
                                );
                            })}
                        </List>
                    </>
                )}
                <MarkdownRenderer>
                    {fileTypeData.description}
                </MarkdownRenderer>
            </Paper>
        </Container>
    );
};